import { useCallback, useState } from 'react'
import { useQueryClient } from 'react-query'
import { showNotification } from '@mantine/notifications'

import { useCreateInitialScriptState } from '@/components/Event/NewScript/hooks/state/useCreateInitialScriptState'
import {
  ScriptActions,
  useScriptActions,
} from '@/components/Event/NewScript/hooks/state/useScriptActions'
import {
  useGetScriptDataQuery,
  useSaveScriptDataMutation,
  useSubmitScriptDataMutation,
} from '@/components/Event/NewScript/queries/scriptData'
import { DRScriptData, Event } from '@/types'
import { getApiErrorMessage } from '@/utils/apiError'

type HookResult = {
  isLoading: boolean
  isError: boolean
  state: DRScriptData | undefined
  actions: ScriptActions
  submitScript: (data: DRScriptData) => Promise<unknown>
  isSubmittingScript: boolean
  refetchState: () => any
}

export const useScriptStateInternal = (event: Event): HookResult => {
  const [stateError, setStateError] = useState<boolean>(false)
  const queryClient = useQueryClient()
  const eventId = event?.id ?? 'unknown'
  const updateCache = useCallback(
    (data: DRScriptData) => {
      queryClient.setQueryData(['script_data', eventId], data)
    },
    [queryClient, eventId],
  )
  const getCachedState = useCallback((): DRScriptData => {
    const cached = queryClient.getQueryData<DRScriptData>([
      'script_data',
      eventId,
    ])

    if (cached === undefined) {
      throw new Error(`Script state missing from cache for event ${eventId}`)
    }

    return cached
  }, [queryClient, eventId])
  const { initScriptData, isLoading: isInitializing } =
    useCreateInitialScriptState({ event })
  const {
    data: state,
    isLoading,
    refetch: refetchState,
  } = useGetScriptDataQuery(event, {
    enabled: !!event,
    staleTime: Infinity,
    onError: error => {
      if (error.response?.status === 404) {
        return initScriptData()
          .then(() => {
            refetchState()
          })
          .catch(() => {
            setStateError(true)
          })
      }
    },
  })

  const { mutate: saveScriptData } = useSaveScriptDataMutation(event!, {
    onError: error => {
      showNotification({
        color: 'red',
        title: 'Error',
        message: getApiErrorMessage(
          error,
          'Could not upload the script, please refresh the page and try again',
          'Could not upload the script',
        ),
      })
      // The optimistic cache update has already overwritten the previous state,
      // so the mutation's built-in rollback is a no-op. Refetch the canonical
      // server state to recover the UI to a consistent state.
      refetchState()
    },
  })

  const { mutateAsync: submitScriptMutation, isLoading: isSubmittingScript } =
    useSubmitScriptDataMutation(event!, {
      onError: error => {
        showNotification({
          color: 'red',
          title: 'Error',
          message: getApiErrorMessage(
            error,
            'Could not submit the script, please refresh the page and try again',
            'Could not submit the script',
          ),
        })
      },
    })

  const { actions, cancelPendingUpload } = useScriptActions({
    uploadState: saveScriptData,
    updateCache,
    getState: getCachedState,
  })

  const submitScript = (data: DRScriptData) => {
    cancelPendingUpload()
    return submitScriptMutation({ ...data, submitted: true })
  }

  return {
    isLoading: isLoading || isInitializing,
    isError: stateError,
    state,
    actions,
    submitScript,
    isSubmittingScript,
    refetchState,
  }
}

export { useScriptState } from '@/components/Event/NewScript/hooks/state/ScriptProvider'
